if (!customElements.get('how-to-apply')) {
  customElements.define(
    'how-to-apply',
    class HowToApply extends HTMLElement {
      constructor() {
        super();

        this.swiper = null;
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.videoListeners = [];

        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleMotionPreference = this.handleMotionPreference.bind(this);
      }

      connectedCallback() {
        this.swiperElement = this.querySelector('.how-to-apply__swiper');
        this.videoCards = Array.from(this.querySelectorAll('.how-to-apply__media-card'));

        this.setupVideoInteractions();
        this.syncSwiper();
        this.setupViewportObserver();

        this.mobileMediaQuery.addEventListener('change', this.handleViewportChange);
        this.reducedMotionQuery.addEventListener('change', this.handleMotionPreference);
        window.addEventListener('resize', this.handleViewportChange);

        this.handleViewportChange();
      }

      disconnectedCallback() {
        this.mobileMediaQuery.removeEventListener('change', this.handleViewportChange);
        this.reducedMotionQuery.removeEventListener('change', this.handleMotionPreference);
        window.removeEventListener('resize', this.handleViewportChange);

        if (this.viewportObserver) {
          this.viewportObserver.disconnect();
          this.viewportObserver = null;
        }

        this.videoListeners.forEach(({ element, eventName, listener }) => {
          element.removeEventListener(eventName, listener);
        });
        this.videoListeners = [];

        this.destroySwiper();
      }

      handleViewportChange() {
        if (this.mobileMediaQuery.matches && this.swiper) {
          this.updateSwiperOffsets();
        } else {
          this.syncSwiper();
        }

        this.updatePlaybackByViewport();
      }

      syncSwiper() {
        if (this.mobileMediaQuery.matches) {
          this.initSwiper();
        } else {
          this.destroySwiper();
        }
      }

      initSwiper() {
        if (this.swiper || !this.swiperElement || typeof Swiper === 'undefined') return;

        this.swiper = new Swiper(this.swiperElement, {
          speed: 500,
          slidesPerView: 'auto',
          spaceBetween: 4,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: this.getMobileEdgePadding(),
          grabCursor: true,
          watchOverflow: true,
          resistanceRatio: 0.85,
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          on: {
            init: () => {
              this.updatePlaybackByViewport();
            },
            slideChange: () => {
              this.updatePlaybackByViewport();
            },
            transitionEnd: () => {
              this.updatePlaybackByViewport();
            },
          },
        });

        this.updateSwiperOffsets();
      }

      destroySwiper() {
        if (!this.swiper) return;

        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      updateSwiperOffsets() {
        if (!this.swiper) return;

        this.swiper.params.slidesOffsetAfter = this.getMobileEdgePadding();
        this.swiper.update();
      }

      getMobileEdgePadding() {
        if (!this.swiperElement) return 0;

        const styles = window.getComputedStyle(this.swiperElement);
        return parseFloat(styles.paddingLeft) || 0;
      }

      setupVideoInteractions() {
        this.videoCards.forEach((card) => {
          const video = card.querySelector('.how-to-apply__video');
          const mediaShell = card.querySelector('.how-to-apply__media-shell');

          if (!video || !mediaShell) return;

          video.controls = false;
          video.muted = true;

          const handleMouseEnter = () => {
            if (this.mobileMediaQuery.matches || this.reducedMotionQuery.matches) return;
            this.playVideo(video);
          };

          const handleMouseLeave = () => {
            if (this.mobileMediaQuery.matches) return;
            this.pauseVideo(video);
          };

          this.addTrackedListener(mediaShell, 'mouseenter', handleMouseEnter);
          this.addTrackedListener(mediaShell, 'mouseleave', handleMouseLeave);
        });
      }

      setupViewportObserver() {
        if (!('IntersectionObserver' in window)) return;

        this.viewportObserver = new IntersectionObserver(
          (entries) => {
            if (!this.mobileMediaQuery.matches || this.reducedMotionQuery.matches) return;

            entries.forEach((entry) => {
              const video = entry.target.querySelector('.how-to-apply__video');
              if (!video) return;

              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                this.playVideo(video);
              } else {
                this.pauseVideo(video);
              }
            });
          },
          {
            threshold: [0.25, 0.6, 0.8],
          }
        );

        this.videoCards.forEach((card) => {
          this.viewportObserver.observe(card);
        });
      }

      updatePlaybackByViewport() {
        if (this.reducedMotionQuery.matches) {
          this.pauseAllVideos();
          return;
        }

        if (!this.mobileMediaQuery.matches) {
          this.pauseAllVideos();
          return;
        }

        if (!this.videoCards.length) return;

        if ('IntersectionObserver' in window) {
          return;
        }

        const targetIndex = this.swiper ? this.swiper.activeIndex : 0;

        this.videoCards.forEach((card, index) => {
          const video = card.querySelector('.how-to-apply__video');
          if (!video) return;

          if (index === targetIndex) {
            this.playVideo(video);
          } else {
            this.pauseVideo(video);
          }
        });
      }

      handleMotionPreference() {
        if (this.reducedMotionQuery.matches) {
          this.pauseAllVideos();
          return;
        }

        this.updatePlaybackByViewport();
      }

      playVideo(video) {
        video.play().catch(() => {});
      }

      pauseVideo(video) {
        video.pause();
      }

      pauseAllVideos() {
        this.videoCards.forEach((card) => {
          const video = card.querySelector('.how-to-apply__video');
          if (!video) return;
          this.pauseVideo(video);
        });
      }

      addTrackedListener(element, eventName, listener) {
        element.addEventListener(eventName, listener);
        this.videoListeners.push({ element, eventName, listener });
      }
    }
  );
}